import includes from 'lodash/includes';

import { getContentItemType } from './page_content_utils.js';
import { isTilePlayable, doesTileLinkToVideo } from './tile_utils.js';
import {
  TILE_ACTION_DATA_VIDEO_INLINE,
  TILE_ACTION_INDICATOR_DEFAULT
} from 'zoomforth_codesbane_viewer/const/tile_actions.js';

import {
  LINK_TYPE_NONE,
  LINK_TYPE_INTERNAL,
  LINK_TYPE_EXTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';


/**
  
  Given state data and contentItem build only the portion of tile data
  necessary to call the `getActionIndicatorTypeFromTileData` method.
  Call and return that method.

*/
export const getActionIndicatorType = (state, contentItem, options={})=> {

  const itemType = getContentItemType(contentItem);

  const actionTypeName = getActionTypeNameLegacy(contentItem);
  const actionData = contentItem.tile_action_data;
  const tileActionIndicatorIcon = options.ignoreTileActionIndicatorIcon ? null : contentItem.tile_action_indicator_icon;

  return getActionIndicatorTypeFromTileData({
    itemType,
    actionTypeName,
    actionData,
    tileActionIndicatorIcon,
    isTilePlayable: isTilePlayable(contentItem),
    doesTileLinkToVideo: doesTileLinkToVideo(state, contentItem)
  });
};

/**
  
  Return a string indicating what type of action is indicated for this tile.
  This string is used to render hover states and the helper icon in the tile corner.

  This is legacy dependency because these strings are rendered into customer pages.

*/
export const getActionIndicatorTypeFromTileData = ({ 
  itemType, 
  actionTypeName, 
  actionData, 
  isTilePlayable, 
  doesTileLinkToVideo,
})=> {
  if (isTilePlayable || doesTileLinkToVideo) {
    return 'video';
  }
  if (itemType === 'other') {
    return 'other';
  }
  if (actionTypeName === 'no') {
    return null;
  }
  if (actionTypeName === TILE_ACTION_DATA_VIDEO_INLINE) {
    return TILE_ACTION_DATA_VIDEO_INLINE;
  }
  if (!actionData) {
    if (itemType === 'pdf') {
      return 'pdf';
    } else if (itemType === 'ppt') {
      return 'ppt';
    } else if (includes(['html_package', 'iframe_embed'], itemType)) {
      return 'embed';
    } else {
      return 'photo';
    }
  }
  if (actionData.type === LINK_TYPE_NONE) {
    return null;
  } else if (actionData.type === LINK_TYPE_EXTERNAL) {
    return 'link';
  } else if (actionData.type === LINK_TYPE_INTERNAL) {
    return 'internal-link';
  }
  throw new Error('Unexpected action data type: ' + actionData.type);
};


export const getActionTypeNameLegacy = (contentItem) => {
  const itemType = getContentItemType(contentItem);
  if (itemType === 'other') {
    return 'link';
  }
  if (itemType === 'form'){
    return 'no';
  }
  if (!contentItem.tile_action_data && itemType === 'text') {
    return 'no';
  }
  if (!contentItem.tile_action_data) {
    return 'overlay';
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_NONE) {
    return 'no';
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_INTERNAL) {
    return 'pagecontentlink';
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_EXTERNAL) {
    return 'link';
  }
  if (contentItem.tile_action_data.type === TILE_ACTION_DATA_VIDEO_INLINE) {
    return TILE_ACTION_DATA_VIDEO_INLINE;
  }
  throw new Error(
    `no action type name could be determined for ${contentItem.uuid}, ensure tile_action_data is not corrupted`
  );
};



export const getActionTypeModern = (contentItem) => {
  const itemType = getContentItemType(contentItem);
  if (itemType === 'other') {
    return 'download';
  }
  if (itemType === 'form'){
    return LINK_TYPE_NONE;
  }
  if (!contentItem.tile_action_data && itemType === 'text') {
    return LINK_TYPE_NONE;
  }
  if (!contentItem.tile_action_data) {
    return 'overlay';
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_NONE) {
    return LINK_TYPE_NONE;
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_INTERNAL) {
    return LINK_TYPE_INTERNAL;
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_EXTERNAL) {
    return LINK_TYPE_EXTERNAL;
  }
  if (contentItem.tile_action_data.type === TILE_ACTION_DATA_VIDEO_INLINE) {
    return TILE_ACTION_DATA_VIDEO_INLINE;
  }
  throw new Error(
    `no action type name could be determined for ${contentItem.uuid}, ensure tile_action_data is not corrupted`
  );
};


export const getAppAction = (contentItem) => {
  const itemType = getContentItemType(contentItem);
  if (itemType === 'other') {
    return {
      type: 'download',
      contentUuid: contentItem.uuid
    };
  }
  if (itemType === 'form'){
    return {
      type: LINK_TYPE_NONE
    };
  }
  if (!contentItem.tile_action_data && itemType === 'text') {
    return {
      type: LINK_TYPE_NONE
    };
  }
  if (!contentItem.tile_action_data) {
    return {
      type: 'overlay',
      contentUuid: contentItem.uuid
    };
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_NONE) {
    return {
      type: LINK_TYPE_NONE
    };
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_INTERNAL) {
    return {
      type: LINK_TYPE_INTERNAL,
      subpageId: contentItem.tile_action_data.subpageId,
      sectionId: contentItem.tile_action_data.sectionId,
      contentUuid: contentItem.tile_action_data.contentUuid
    };
  }
  if (contentItem.tile_action_data.type === LINK_TYPE_EXTERNAL) {
    return {
      type: LINK_TYPE_EXTERNAL,
      url: contentItem.tile_action_data.url,
      target: contentItem.tile_action_data.target
    };
  }
  if (contentItem.tile_action_data.type === TILE_ACTION_DATA_VIDEO_INLINE) {
    return TILE_ACTION_DATA_VIDEO_INLINE;
  }
  throw new Error(
    `no action type name could be determined for ${contentItem.uuid}, ensure tile_action_data is not corrupted`
  );
};

/*
  The reverse operation of `getActionTypeModern`
*/
export const getTileActionData = (contentItem, actionType)=> {
  const itemType = getContentItemType(contentItem);
  if (actionType === 'download' && itemType === 'other') {
    return null;
  }
  if (actionType === LINK_TYPE_NONE && itemType === 'form'){
    return null;
  }
  if (actionType === LINK_TYPE_NONE && itemType === 'text') {
    return null;
  }
  if (actionType === 'overlay') {
    return null;
  }
  if (actionType === LINK_TYPE_NONE) {
    return { type:LINK_TYPE_NONE };
  }
  if (actionType === LINK_TYPE_INTERNAL) {
    return { type:LINK_TYPE_INTERNAL };
  }
  if (actionType === LINK_TYPE_EXTERNAL) {
    return { type: LINK_TYPE_EXTERNAL };
  }
  throw new Error(
    `No tile_action_data could be determined for content with uuid=${contentItem.uuid} and actionType=${actionType}.`
  );
};
  
